import { action, computed, observable } from 'mobx'
import { computedFn } from 'mobx-utils'
import { ApiProjectTypes, ApiSeriesTypes, GenreTypes, Project, ProjectDTO, ProjectTypes, StatusTypes } from 'models'
import { PROJECTS_TABLE_STORE } from 'store/ProjectsTableStore'
import { RootStore } from 'store/RootStore'
import { Api } from 'utilities'
import { GENERAL_STORE, ModalComponentKeyTypes } from '../GeneralStore'

export const PROJECT_STORE = 'PROJECT_STORE'

export class ProjectStore {
  @observable projectsList: Project[] = []
  @observable projectsFlatList: Project[] = []
  @observable vovProjectList: Project[] = []
  @observable projectToView: Project = {} as Project
  @observable parentProjectDetails: Project = {} as Project
  @observable genresFilter: GenreTypes[] = Object.values(GenreTypes)
  @observable statusFilter: StatusTypes[] = Object.values(StatusTypes)
  @observable dateAddedPeriodFilter: any = null

  rootStore: RootStore
  projectIntervalId: any

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  @computed get parentType () {
    return this.parentProjectDetails
      ? this.parentProjectDetails.type || null
      : null
  }

  @computed get projectTypeOptions () {
    switch (this.parentType) {
      case ApiProjectTypes.TV_SERIES:
        return [ApiSeriesTypes.TV_SEASON]

      case ApiSeriesTypes.TV_SEASON:
        return [ApiSeriesTypes.TV_EPISODE]

      case ApiProjectTypes.MOVIE:
        return [ApiProjectTypes.MOVIE]

        default:
        return Object.values(ProjectTypes)
    }
  }

  @computed get areAllGenresInTheFilter (): boolean {
    return this.genresFilter.length === Object.keys(GenreTypes).length
  }

  @observable getProjectByIdFromFlatList = computedFn(id => {
    return this.projectsFlatList.find(p => p.id === id)
  });

  @observable getProjectByIdFromVovList = computedFn(id => {
    return this.vovProjectList.find(p => p.id === id)
  });

  @action.bound setFilters (genresFilter, statusFilter, dateAddedPeriodFilter) {
    this.genresFilter = genresFilter
    this.statusFilter = statusFilter
    this.dateAddedPeriodFilter = dateAddedPeriodFilter
  }

  @action.bound setVovProjectList (list: Project[]) {
    this.vovProjectList = list
  }

  @action.bound setProjectToView (projectDetails: any = {}) {
    this.projectToView = projectDetails || {}
  }

  @action.bound setParentProjectDetails (projectDetails: any = {}) {
    this.parentProjectDetails = projectDetails
  }

  @action.bound setProjectsList (list: Project[] = []) {
    this.projectsList = list
  }

  @action.bound setProjectsFlatList (list: Project[] = []) {
    this.projectsFlatList = list
  }

  getProjects = async (loadHierarchy = false, silentLoading = false) => {
    const {
      setLoadingOn, setLoadingOff, handleApiError
    } = this.rootStore[GENERAL_STORE]

    !silentLoading && setLoadingOn()
    let projects: Project[] = []
    await Api
      .get(`/project/all?loadHierarchy=${loadHierarchy}`)
      .then((data: ProjectDTO[]) => {
        projects = data.map(p => new Project(p))
        loadHierarchy
          ? this.setProjectsList(projects)
          : this.setProjectsFlatList(projects)
        setLoadingOff()
      })
      .catch(handleApiError)
    return projects
  }

  getVovProjects = () => {
    this.getProjectsByType(ApiProjectTypes.VOV)
      .then(this.setVovProjectList)
      .catch(this.rootStore[GENERAL_STORE].handleApiError)
  }

  getRelatedVovProjects = (projectId: Number) => {
    this.getRelatedProjectsByType(ApiProjectTypes.VOV, projectId)
      .then(this.setVovProjectList)
      .catch(this.rootStore[GENERAL_STORE].handleApiError)
  }

  private getProjectsByType (type: ApiProjectTypes) {
    return Api
      .post('/project/getByType', [type])
  }

  private getRelatedProjectsByType (type:ApiProjectTypes, projectId:Number) {
    return Api
      .get(`/project/${projectId}/related/${type}`)
  }

  republishProject = async (projectId: number) => {
    const {
      setLoadingOn, setLoadingOff, handleApiError
    } = this.rootStore[GENERAL_STORE]

    setLoadingOn()
    return Api
      .get(`/project/${projectId}/republish`)
      .then((data) => {
        this.rootStore[PROJECT_STORE].changeStatus(projectId, StatusTypes.PUBLISHED)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  createProject = (projectDetails: ProjectDTO) => {
    const {
      setLoadingOn, push, showSuccess, handleApiError
    } = this.rootStore[GENERAL_STORE];
    setLoadingOn();
      const { actors, directors, producers, relatedMedia, brands, ...project } = projectDetails;
  
    Api.post('/project/create', project)
      .then((data: ProjectDTO) => {
        if (
          actors.length ||
          directors.length ||
          producers.length ||
          relatedMedia.length ||
          brands.length
        ) {
          return Api.post('/project/update', {
            ...data,
            actors: this.removeDuplicates(actors),
            directors: this.removeDuplicates(directors),
            producers: this.removeDuplicates(producers),
            relatedMedia: this.removeDuplicates(relatedMedia),
            brands // It's not affected here
          });
        }
  
        return Promise.resolve(data);
      })
      // .then(this.triggerVideoProcessing)  
      .then((project:ProjectDTO) => {
        // this.getProjects(); It's not affected
        showSuccess('Project has been created.');
        push(`/projects/edit/${project?.id && project?.id}`);
      })
      .catch(handleApiError);
  }
  
  removeDuplicates = (items: { id: string | number }[]) => {
    const uniqueMap = new Map();
    items.forEach(item => uniqueMap.set(item.id,{ id : item.id}));
    return Array.from(uniqueMap.values()); 
  };
  

  // triggerVideoProcessing = (projectDetails: ProjectDTO) => {
  //   const { videoEntityId, videoUrl, id: projectId } = projectDetails
  //   const {
  //     handleApiError
  //   } = this.rootStore[GENERAL_STORE]

  //   if (videoEntityId && videoUrl && projectId) {
  //     Api
  //       .post(
  //         '/editor/manage',
  //         { videoEntityId, videoUrl, projectId },
  //         'videoProcessingApi'
  //       )
  //       .catch(handleApiError)
  //   }

  //   return new Promise((resolve) => resolve(projectDetails))
  // }
  triggerVideoProcessing = (projectDetails: ProjectDTO) => {
    const { videoEntityId, videoUrl, id: projectId, autoGroupAccuracy } = projectDetails
    const { handleApiError } = this.rootStore[GENERAL_STORE]
    return new Promise((resolve, reject) => {
      if (videoEntityId && videoUrl && projectId) {
        Api.post('/editor/manage',
          {
            videoEntityId,
            videoUrl,
            projectId,
            // autoGroupAccuracy: autoGroupAccuracy || null,
            autoGroupAccuracy: 0.1,
            // isAutoGroup: autoGroupAccuracy != null
            isAutoGroup: true
          }, 'videoProcessingApi')
          .then(() => {
            resolve(projectDetails)
          })
          .catch((error) => {
            window?.location?.reload();
            reject(error)
            handleApiError(error)
          })
      } else {
        // If any of the required parameters is missing, reject with an error
        const missingParamError = new Error('Missing required parameters')
        reject(missingParamError)
      }
    })
  };

  handleVideoProcessingTrigger = (projectDetails: ProjectDTO) => {
    const { videoUrl: prevVideoUrl, id: projectId } = this.projectToView
    const { videoUrl: nextVideoUrl, videoEntityId } = projectDetails
    if (!prevVideoUrl && nextVideoUrl && videoEntityId && projectId) {
      return this.triggerVideoProcessing(projectDetails)
    }
    return new Promise((resolve) => resolve(projectDetails))
  }

  getProjectById = async (
    id: string | number,
    loadContents = true,
    saveAsParent = false,
    loadHierarchy = false
  ) => {
    const {
      setLoadingOn, setLoadingOff, handleApiError
    } = this.rootStore[GENERAL_STORE]

    setLoadingOn()

    await Api
      .get(`/project/${id}/view?loadContents=${loadContents}&loadHierarchy=${loadHierarchy}`)
      .then((data: any) => {
        setLoadingOff()

        const manageVideoStatus = data.manageVideoStatus; 
        localStorage.setItem('manageVideoStatus', manageVideoStatus);
        localStorage.setItem('readyForEdit', JSON.stringify(data.readyForEdit));
  
        if (saveAsParent) {
          return Promise.resolve(data)      }

        this.setProjectToView(new Project(data))
  
        return data.parentId
          ? Api
            .get(`/project/${data.parentId}/view?loadContents=false&&loadHierarchy=${loadHierarchy}`)
          : Promise.resolve({})
      })
      .then((parent: ProjectDTO) => {
        return parent.parentId
          ? Api
            .get(`/project/${parent.parentId}/view?loadContents=false&&loadHierarchy=${loadHierarchy}`)
          : Promise.resolve(parent)
      })
      .then(this.setParentProjectDetails)
      .catch(handleApiError)
  
    this.pollManageVideoStatus(id);
  }
  
  pollManageVideoStatus = async (id: string | number) => {
    const checkStatusInterval = setInterval(async () => {
      try {
        const status = localStorage.getItem('manageVideoStatus');
        if (status === 'COMPLETED') {
          clearInterval(checkStatusInterval);  
        } else if (status === 'PROCESSING') {
          await this.checkVideoStatus(id);
        }
      } catch (error) {
        console.error('Error checking video status:', error);
        clearInterval(checkStatusInterval);
      }
    }, 10000);
  }
    checkVideoStatus = async (id: string | number) => {
    try {
      const response:any = await Api.get(`/project/${id}/view?loadContents=false&loadHierarchy=false`);
      const manageVideoStatus = response.manageVideoStatus;
      localStorage.setItem('manageVideoStatus', manageVideoStatus);  
    } catch (error) {
      console.error('Error fetching video status:', error);
    }
  }
  
  getManageVideoStatusFromStorage = () => {
    return localStorage.getItem('manageVideoStatus');
  };
  
  clearProjectToView = () => {
    this.setProjectToView()
  }

  clearParentProjectDetails = () => {
    this.setParentProjectDetails()
  }

  clearProjectsList = () => {
    this.setProjectsList()
  }

  clearProjectsFlatList = () => {
    this.setProjectsFlatList()
  }

  updateProject = (body) => {
    return Api
      .post('/project/update', {
        ...body,
        actors: body.actors?.map(({ id }) => ({ id })),
        directors: body.directors?.map(({ id }) => ({ id })),
        producers: body.producers?.map(({ id }) => ({ id }))
      })
      .catch(this.rootStore[GENERAL_STORE].handleApiError)
  }

  editProject = (projectDetails: ProjectDTO) => {
    if (this.projectToView.id !== projectDetails.id) {
      this.rootStore[GENERAL_STORE]
        .showError('Could not gather all details for the for the project.')
    }

    const {
      setLoadingOn,
      handleApiError,
      push,
      showSuccess
    } = this.rootStore[GENERAL_STORE]

    setLoadingOn()

    projectDetails = {
      ...this.projectToView,
      ...projectDetails
      // parentId: 9,
      // type: 'TV Episode',
      // subtype: 'TV Episode'
    }
    // return;
    this.updateProject(projectDetails)
      .then(this.handleVideoProcessingTrigger)
      .then(() => {
        this.clearProjectToView()
        this.clearProjectsList()
        push('/projects')
        showSuccess('Details saved.')
      })
      .catch(handleApiError)
  }

  changeStatus = (projectId: number, status: StatusTypes) => {
    const {
      [GENERAL_STORE]: {
        handleApiError
      },
      [PROJECTS_TABLE_STORE]: {
        triggerSearch
      }
    } = this.rootStore

    Api
      .get(`/project/${projectId}/changeStatus/${status}`)
      .then((project: Project) => {
        const cloneProjects = [...this.projectsList]

        const updateStatus = (projects: Project[]) => {
          projects.forEach(p => {
            if (p.id === project.id) {
              p.status = project.status
            } else {
              p.children && updateStatus(p.children)
            }
          })
        }

        updateStatus(cloneProjects)

        this.setProjectsList(cloneProjects)
        triggerSearch({ forceClearResultsFirst: true })
      })
      .catch(handleApiError)
  }

  triggerNewVideoProcessingForPtojectToView = () => {
    // return this.triggerVideoProcessing(this.projectToView.asDTO)
    return 1
  }

  openFilterProjectsModal = () => {
    this.rootStore[GENERAL_STORE].setDetailsAndShowModalDialog({
      title: 'Filter projects',
      componentKey: ModalComponentKeyTypes.PROJECTS_FILTER
    })
  }

  changeAutoObjectDetection = async (payload:{id : number, autoObjectDetection : boolean})
  :Promise<boolean> => {
    const data = await Api.post('/project/isCheckProcess', {
      ...payload
    })
    if (!data) return false
    return true
  }

  updateAutoObjectDetection = ({ id, autoObjectDetection }:{id : number, autoObjectDetection : boolean})
  :Promise<boolean> => {
    const payload = {
      id,
      autoObjectDetection
    }
    return this.changeAutoObjectDetection(payload)
  }
}
